import { api } from '../lib/api-client';

/**
 * Get all pension contributions
 * @returns {Promise} Promise resolving to contributions data
 */
export const getAllPensionContributions = async () => {
  try {
    const response = await api.get('/contributions');
    // Normalize response shapes so UI code can rely on `response.data` being an array
    const raw = response.data;

    // If backend already returns { data: [...] } keep it
    if (raw && Array.isArray(raw.data)) {
      return raw;
    }

    // If backend returns a single transaction_history object (new shape), map it to an array
    // Example incoming shape:
    // { success: true, data: { transaction_history: { ... } } }
    if (raw && raw.data && raw.data.transaction_history) {
      const th = raw.data.transaction_history;

      // Create a single contribution-like object that the UI expects
      const contributionObj = {
        id: th.member_number ? `member-${th.member_number}` : 'member-1',
        scheme_name: th.scheme_name || 'Pension Account',
        name: th.scheme_name || 'Pension Account',
        contribution_number: th.member_number || null,
        status: th.status || 'A',
        amount: th.account_value || th.total_contribution_tally || 0,
        total_contributions: th.total_contribution_tally || 0,
        employer_contribution: th.employer_contribution || 0,
        transfer_received: th.transfer_received || 0,
        total_redemption: th.total_redemption || 0,
        gain_loss: th.gain_loss || 0,
        current_price: th.current_price || null,
        date_joined: th.date_joined || null,
        units_bf: th.units_bf || null,
        transactions: Array.isArray(th.transactions) ? th.transactions : [],
        pagination: th.pagination || null,
        // Keep raw for any consumers that need full details
        _raw: th
      };

      return { data: [contributionObj], success: raw.success, message: raw.message };
    }

    // Fallback: if raw itself is an array, return as { data: raw }
    if (Array.isArray(raw)) {
      return { data: raw };
    }

    // Otherwise, return whatever we have under data if available, or empty array
    return { data: raw && raw.data ? raw.data : [] };
  } catch (error) {
    console.error('Error fetching pension contributions:', error);
    throw error;
  }
};

/**
 * Get specific pension contribution by ID
 * @param {string|number} contributionId - The contribution ID
 * @returns {Promise} Promise resolving to specific contribution data
 */
export const getPensionContributionById = async (contributionId) => {
  try {
    const response = await api.get(`/contributions/${contributionId}`);
    const raw = response.data;

    // New API may return { data: { transaction_history: { ... } } }
    if (raw && raw.data && raw.data.transaction_history) {
      // Return the transaction_history object directly to callers expecting detail
      return raw.data.transaction_history;
    }

    // If response.data has a data property (old shape), return it
    if (raw && raw.data) return raw.data;

    // Otherwise return raw
    return raw;
  } catch (error) {
    console.error(`Error fetching pension contribution ${contributionId}:`, error);
    throw error;
  }
};

export const getPensionTransactions = async (filters = {}) => {
  try {
    const response = await api.get('/contributions', {
      params: {
        month: filters.month ?? '',
        year: filters.year ?? '',
        policy_number: filters.policy_number || filters.policyNumber || '',
        scheme_id: filters.scheme_id || filters.schemeId || '',
        amount: filters.amount ?? '',
        reference: filters.reference ?? ''
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching pension transactions:', error);
    throw error;
  }
};
